<template>
  <div :class="['LocatieInformatie__Content', { isLockedForEditing }]">
    <b-alert
      :show="isLockedForEditing && !isRealizedAndDifferentCpo"
      variant="warning"
    >
      {{ $t(isLockedForEditingMessage, { case: processCaseRef }) }}
    </b-alert>
    <b-alert
      :show="cpoWithWrongConfig"
      variant="warning"
    >
      Wrong/no Cpo user organization!
    </b-alert>
    <div v-if="!isLoading">
      <b-alert
        :show="error !== false"
        variant="danger"
      >
        {{ error }}
      </b-alert>
      <HintAddressIncomplete v-if="!isLockedForEditing" />

      <Form
        :class="{ isLockedForEditing }"
        @submit="handleSubmitUpdate"
      >
        <div>
          <FormField
            v-model="fields.remark.value"
            v-bind="fields.remark"
          />
          <!-- start address -->
          <div v-if="isAddressEditable && !isLockedForEditing">
            <strong> {{ $t('address.title') }} </strong><br>
            <FormField
              v-model.trim="fields.address.street.value"
              v-bind="fields.address.street"
              @input="fields.address.edited = true"
            />
            <div class="row">
              <div class="col-6">
                <FormField
                  v-model.trim="fields.address.number.value"
                  v-bind="fields.address.number"
                  @input="fields.address.edited = true"
                />
              </div>
              <div class="col-6">
                <FormField
                  v-model.trim="fields.address.suffix.value"
                  v-bind="fields.address.suffix"
                  @input="fields.address.edited = true"
                />
              </div>
            </div>
            <div class="row">
              <div class="col-5">
                <FormField
                  v-model.trim="fields.address.postalcode.value"
                  v-bind="fields.address.postalcode"
                  @input="fields.address.edited = true"
                />
              </div>
              <div class="col-7">
                <FormField
                  v-model.trim="fields.address.city.value"
                  v-bind="fields.address.city"
                  @input="fields.address.edited = true"
                />
              </div>
            </div>
          </div>

          <template v-else-if="isLockedForEditing">
            <p>
              <strong>{{ $t('address.title') }}</strong><br>
              {{ address.simple_address }}<br>
              {{ address.postalcode }} {{ address.city }}
            </p>
          </template>
          <template v-else>
            <p>
              <strong>
                {{ $t('address.title') }}
                <SvgIcon
                  id="ecomovement-hint"
                  icon="info-circle-regular"
                />
              </strong> <br>
              {{ address.simple_address }} <br>
              {{ address.postalcode }} {{ address.city }}
            </p>
            <b-tooltip
              target="ecomovement-hint"
              triggers="hover"
            >
              EcoMovement data kan niet worden aangepast.
            </b-tooltip>
          </template>

          <p v-if="address && address.source">
            <strong>{{ $t('components.sidebar.LocatieInformatieContent.addressSource') }}</strong> <br>
            {{ addressSource }}
          </p>
          <!-- end address -->

          <p v-if="evse">
            <strong>{{ $t('evse_location_name') }}</strong><br>
            {{ evse }}
          </p>

          <p v-if="evse_ids.length">
            <strong>{{ $t('evse_ids') }}</strong><br>
            {{ evse_ids.join(', ') }}
          </p>

          <p v-if="coordinates.lat && coordinates.lng">
            <strong>
              {{ $t('address.coordinates') }}
              <SvgIcon
                v-if="importedWithoutCoordinates"
                id="no-coordinates-hint"
                icon="info-circle-regular"
              />
            </strong> <br>
            {{ coordinates.lat }}, <br>
            {{ coordinates.lng }}
          </p>
          <b-tooltip
            v-if="importedWithoutCoordinates"
            target="no-coordinates-hint"
            triggers="hover"
          >
            Deze locatie werd door Vattenfall zonder coördinaten aangeleverd.
          </b-tooltip>

          <p v-if="introducedBy">
            <strong>{{ $t('introducedBy') }}</strong><br>
            {{ introducedBy }}
          </p>

          <p v-if="parkinglotIds">
            <strong>{{ $t('mapLayersPanel.municipality.parkingSpacesAmsterdam.title') }}</strong><br>
            {{ parkinglotIds }}
          </p>

          <div v-if="prio && prio.order">
            <strong>{{ $t('components.sidebar.PlanModeSearch.priority.label') }}</strong>
            <p>{{ prio.order }}</p>
          </div>

          <FormField
            v-model="fields.fase.value"
            v-bind="fields.fase"
            :options="faseOptions"
            :style="!isRealizedAndDifferentCpo ? 'pointer-events: auto' : ''"
            @input="fields.fase.edited=true"
          />

          <p v-if="power">
            <strong>{{ $t('components.sidebar.PlanModeSearch.power') }}</strong><br>
            {{ power | formatTokW }}
          </p>

          <p v-if="nroutlets">
            <strong>Aantal laadpunten</strong><br>
            {{ nroutlets }}
          </p>

          <p v-if="types">
            <strong>{{ $t('components.sidebar.LocatieInformatieContent.connectorTypes') }}</strong><br>
            <span
              v-for="type of types"
              :key="type"
            >{{ type }}<br></span>
          </p>

          <template v-if="isLockedForEditing">
            <p>
              <strong>{{ $t('mapLayersPanel.status.title') }}</strong><br>
              {{ chargingPointLabel }}
            </p>
          </template>
          <template v-else>
            <FormField
              v-model="chargingPointCategory"
              v-bind="fields.status"
              :disabled="!isAllowedToEditStatus"
              :options="statusOptions"
              :info="statusInfo"
              @input="setChargingPointCategory"
            />
            <div v-if="groupLayers.length > 1">
              <strong
                class="d-flex justify-content-center"
                :style="chargingPointLabelStyle"
              >
                {{ chargingPointLabel }}
              </strong>
              <ChargingpointsLayerGroup
                :layer="layer"
                :group-layers="groupLayers"
                :selected-status="selectedStatus"
                :is-legend="false"
                @input="handleInput"
              />
            </div>
          </template>

          <div>
            <strong> {{ $t('cpo') }} </strong>
            <p
              v-if="isLockedForEditing"
              class="mb-3"
            >
              {{ activeCPOLabel }}
            </p>
            <vSelect
              v-else
              v-model="activeCPO.value"
              class="mb-3"
              :options="cpoSelectOptions"
              :reduce="(option) => option.value"
              :disabled="isCpoFieldDisabled"
            />
          </div>

          <p v-if="isLockedForEditing">
            <strong>{{ fields.owner.label }}</strong><br>
            {{ activeOwnerLabel }}
          </p>
          <FormField
            v-else
            v-model="fields.owner.value"
            v-bind="fields.owner"
            :options="ownerOptions"
            :disabled="ownerOptions.length <= 1"
          />
          <div
            v-if="selectedStatus.includes('private')"
            class="mb-2"
          >
            <template v-if="isLockedForEditing">
              <p>
                <strong>{{ $t("accessibility.public") }}</strong><br>
                {{ publish ? 'Ja' : 'Nee' }}
              </p>
              <p>
                <strong>{{ $t('private_network') }}</strong><br>
                {{ activePublishNetworksLabel }}
              </p>
            </template>
            <template v-else>
              <div class="d-flex align-items-center justify-content-between mb-1">
                <b>{{ $t("accessibility.public") }}</b>
                <ToggleSwitch
                  :value="publish"
                  @input="publishToggle = !publishToggle"
                />
              </div>

              <FormField
                v-model="fields.publish_allowed_to.value"
                v-bind="fields.publish_allowed_to"
                :disabled="networkOptions?.length === 0 || publishToggle === true"
                :options="networkOptions"
              />
            </template>
          </div>
          <p v-if="closingDates">
            <strong>{{ $t('components.sidebar.LocatieInformatieContent.closingDateParticipation') }}</strong><br>
            {{ fields.participation.closingDate }} <br>
          </p>
          <div class="my-4">
            <strong>{{ $t('components.sidebar.LocatieInformatieContent.trafficDecision') }}</strong>
            <template v-if="isLockedForEditing">
              <div
                v-for="(date, key) in fields.trafficDecision"
                :key="key"
              >
                <p class="mb-2">
                  <strong>{{ $t(labels[key]) }}</strong><br>
                  {{ date || '-' }}
                </p>
              </div>
            </template>
            <template v-else>
              <div
                v-for="(date, key) in fields.trafficDecision"
                :key="key"
              >
                <label
                  :for="key"
                  class="mb-0"
                >{{ $t(labels[key]) }}</label>
                <b-form-datepicker
                  :id="key"
                  v-model="fields.trafficDecision[key]"
                  :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                  :dropup="true"
                  :min="(!isPublicationDate(key) && publicationDate) || ''"
                  :max="(isPublicationDate(key) && definitiveDate) || ''"
                  :locale="$i18n.locale"
                  :placeholder="$t('selectedDate')"
                  reset-button
                  :disabled="!isPublicationDate(key) && !publicationDate"
                  class="mb-2 datepicker"
                  @input="event => handleDatePickerInput(event, key)"
                />
              </div>
            </template>
          </div>
          <AlternativeLocation
            :predecessor="fields.predecessor.value"
            :disabled="isLockedForEditing"
            @select="(value) => (fields.predecessor.value = value)"
          />

          <!-- start locatie beheer -->
          <p>
            <strong>{{ $t('components.sidebar.LocatieInformatieContent.locationManagement') }}</strong>
          </p>
          <template v-if="superuser && !isLockedForEditing">
            <FormField
              v-model="fields.beheerder.value"
              v-bind="fields.beheerder"
              :options="beheerderOptions"
            />
            <div class="d-flex align-items-center add-validator">
              <FormField
                v-model="fields.validatie.value"
                v-bind="fields.validatie"
                :options="validatorOptions"
              />
              <b-icon-plus
                variant="dark"
                class="add-validator__icon u-clickable ml-2 mt-1"
                @click="$_chargingpointValidatorsMixin_addValidator"
              />
            </div>
            <div class="the-validators">
              <ul v-if="newValidators.length > 0">
                <li
                  v-for="(validator, index) in newValidators"
                  :key="validator.user_id"
                  class="mb-1 d-flex"
                >
                  {{ validator.email }}
                  <b-icon-trash
                    variant="dark"
                    class="the-validators__validator__icon u-clickable pull-right"
                    @click="$_chargingpointValidatorsMixin_removeValidator(index)"
                  />
                </li>
              </ul>
              <p
                v-else
                class="text-muted"
              >
                Geen validatoren geselecteerd
              </p>
            </div>
          </template>
          <template v-else>
            <p>
              <strong>{{ $t('administrator') }}</strong><br>
              {{ beheerder ? beheerder.name : $t('none') }}
            </p>
            <div class="the-validators">
              <p class="mb-0">
                <strong>{{ $t('validators') }}</strong>
              </p>
              <ul v-if="hasValidators">
                <li
                  v-for="validator in validators"
                  :key="validator.user_id"
                >
                  {{ validator.name }}
                </li>
              </ul>
              <span v-else>
                {{ $t('none') }}
              </span>
            </div>
          </template>
          <!-- end locatie beheer -->
        </div>

        <div class="sticky-bottom">
          <div class="mt-2 d-flex justify-content-end">
            <b-button
              v-if="hasLockedFase && locked"
              id="unlock-hint"
              type="submit"
              size="sm"
              variant="primary"
              class="mt-2 ml-auto"
              @click.prevent="unlock"
            >
              <SvgIcon
                class="unlock-hint-icon--white mr-1 mt-"
                icon="unlock-keyhole-solid"
              /> Ontgrendelen om te
              bewerken

              <b-tooltip
                target="unlock-hint"
                triggers="hover"
              >
                Deze laadpaal bevat een fase prioritering. Het is niet de bedoeling dat laadpalen met een fase bewerkt
                worden.
              </b-tooltip>
            </b-button>
            <div v-else>
              <b-button
                v-if="!isLockedForEditing && canRemove"
                :disabled="isLockedForEditing"
                class="mr-1"
                size="sm"
                variant="danger"
                @click.prevent="handleChargingpointDelete"
              >
                Verwijder locatie
              </b-button>
              <b-button
                v-if="faseHasChanged || remarkHasChanged || !isLockedForEditing"
                type="submit"
                size="sm"
                variant="primary"
                style="pointer-events: auto"
              >
                Opslaan
              </b-button>
            </div>
          </div>
        </div>
      </Form>
    </div>
    <p
      v-else
      class="text-muted"
    >
      Loading...
    </p>
  </div>
</template>

<script>
import { validate } from 'uuid'
import { reverseDate } from '@/helpers/date'
import { phaseToArray } from '@/helpers/phases'
import { mapActions, mapGetters } from 'vuex'
import { BIconPlus, BIconTrash } from 'bootstrap-vue'
import Form from '@/components/form/Form'
import FormField from '@/components/form/FormField'
import SvgIcon from '@/components/common/SvgIcon'
import chargingpointDataMixin from '@/mixins/chargingpoint/chargingpointDataMixin'
import chargingpointEditMixin from '@/mixins/chargingpoint/chargingpointEditMixin'
import chargingpointCpoMixin from '@/mixins/chargingpoint/chargingpointCpoMixin'
import chargingpointValidatorsMixin from '@/mixins/chargingpoint/chargingpointValidatorsMixin'
import userMixin from '@/mixins/common/userMixin'
import { SOURCE } from '@/services/sourceTranslation'
import {
  EcoMovementLockedForEditingMessage,
  RHDHVBedrijventerreinenLockedForEditingMessage,
  RHDHVLockedForEditingMessage,
} from '@/services/commonMessages'
import HintAddressIncomplete from '@/components/map/sidebar/HintAddressIncomplete'
import AlternativeLocation from '@/components/map/sidebar/AlternativeLocation'
import { formatFormattedAddress, formatSimpleAddress } from '@/../shared/helpers/address'
import ChargingpointsLayerGroup from '@/components/common/ChargingpointsLayerGroup.vue'
import ToggleSwitch from '@/components/form/ToggleSwitch'
import vSelect from '@/components/form/vSelect'

export default {
  components: {
    AlternativeLocation,
    HintAddressIncomplete,
    Form,
    FormField,
    SvgIcon,
        vSelect,

    BIconPlus,
    BIconTrash,
    ChargingpointsLayerGroup,
    ToggleSwitch,
  },
  mixins: [
    chargingpointDataMixin,
    chargingpointEditMixin,
    chargingpointCpoMixin,
    chargingpointValidatorsMixin,
    userMixin,
  ],
  data() {
    return {
      translationsPath: 'components.sidebar.LocatieInformatieContent',
      error: false,
      publishToggle: null,
      fields: {
        owner: {
          type: 'select',
          label: this.$i18n.t('components.sidebar.LocatieInformatieContent.owner'),
          value: null,
        },
        publish_allowed_to: {
          type: 'v-select',
          label: this.$i18n.t('private_network'),
          value: [],
        },
        address: {
          edited: false,
          street: {
            label: this.$i18n.t('address.street'),
            value: null,
          },
          number: {
            label: this.$i18n.t('address.houseNumber'),
            value: null,
          },
          suffix: {
            label: this.$i18n.t('address.suffix'),
            value: null,
          },
          postalcode: {
            label: this.$i18n.t('address.zipCode'),
            value: null,
          },
          city: {
            label: this.$i18n.t('address.city'),
            value: null,
          },
        },
        power: {
          type: 'number',
          label: this.$i18n.t('components.sidebar.PlanModeSearch.power'),
          value: this.power,
          step: '0.01',
          placeholder: 'e.g. 11.7',
        },
        remark: {
          label: this.$i18n.t('remark'),
          value: this.remark,
          type: 'textarea',
        },
        status: {
          label: this.$i18n.t('mapLayersPanel.status.title'),
          value: 'suggestion',
          type: 'select',
        },
        fase: {
          edited: false,
          label: this.$i18n.t('phase'),
          value: [],
          type: 'v-select',
        },
        predecessor: {
          value: null,
        },
        participation: {
          closingDate: {
            label: this.$i18n.t('components.sidebar.LocatieInformatieContent.closingDateParticipation'),
            value: null,
          },
        },
        trafficDecision: {
          publicationDate: '',
          definitiveDate: '',
        },
      },
      isDragging: false,
      locked: true,
      selectedStatus: 'chargingpoints-suggestion',
      chargingPointLabel: '',
      chargingPointCategory: 'suggestion',
      faseHasChanged: false,
      remarkHasChanged: false,
      labels: {
        publicationDate: '',
        definitiveDate: '',
      },
      activeCPO: { label: '', value: null },

    }
  },
  computed: {
    beheerderOptions() {
      const deleteBeheerder = { value: null, text: this.$t('validationPanel.noAdmin') }
      return [deleteBeheerder, ...this.usersByCodeAsOptions]
    },
    ...mapGetters('planmode', {
      chargingpoint: 'getSelectedChargingpoint',
      getChargingPointByUuid: 'getChargingPointByUuid',
      isPlanModeActive: 'isActive',
      getRejectedLocationUuid: 'getRejectedLocationUuid',
      getPlanmodeLayers: 'getPlanmodeLayers',
      getLockedPhases: 'getLockedPhases',
    }),
    ...mapGetters('access', ['getActiveMunicipality']),
    ...mapGetters('currentUser', {
      currentUserRole: 'getCurrentUserRole',
    }),
    isBoundToProcess() {
      // if (this.chargingpoint.isVattenfallProcess) {
      //   return false
      // } zie TOOLS-6985, Locked is locked
      return !!this.chargingpoint.workflowCaseRef ||
        !!this.chargingpoint.workflowUuid ||
        !!this.chargingpoint.externalProcessId
    },
    isStatusRealized () {
      return this.chargingpoint.properties.status.includes('realized')
    },
    userIdentity () {
      return this.$auth.user['https://evmaps.nl/identity']
    },
    userIdentityCpoUuid () {
      const isCpo = this.userIdentity?.category === 'cpo'
      return isCpo && this.userIdentity.uuid
    },
    cpoWithWrongConfig () {
      return this.currentUserRole === 'cpo' && !this.userIdentityCpoUuid
    },
    isRealizedAndDifferentCpo () {
      return this.isStatusRealized && (this.userIdentityCpoUuid && this.userIdentityCpoUuid !== this.currentCpo?.uuid) || this.cpoWithWrongConfig
    },
    isLockedForEditing() {
      return this.isBoundToProcess || this.chargingpoint.isLockedForEditing || this.isRealizedAndDifferentCpo
    },
    isLockedForEditingMessage() {
      if (this.chargingpoint?.dataSource?.value === SOURCE.ECO_MOVEMENT) {
        return EcoMovementLockedForEditingMessage
      }

      if (this.isBoundToProcess) {
        return 'components.sidebar.Tabs.CommentsTab.editNotAllowed'
      }

      // TOOLS-3285: Vastzetten palen met fase "Bedrijventerreinen GORAL"
      if (this.customPhase === 'Bedrijventerreinen GORAL') {
        return RHDHVBedrijventerreinenLockedForEditingMessage
      }

      return RHDHVLockedForEditingMessage
    },
    processCaseRef() {
      return this.chargingpoint?.workflowCaseRef || this.chargingpoint?.externalProcessId
    },
    hasLockedFase() {
      return phaseToArray(this.chargingpoint.prio?.fase).some(r => this.getLockedPhases.includes(r))
    },
    canRemove() {
      // return this.getEditMode && (isPlanModeAdmin() || (isAnalist() && (this.userName === this.currentUserName) && (this.status.value !== 'definitive')))
      return true // TODO: Add permission check - Role can differ per municipality!
    },
    isAllowedToEditStatus() {
      // superuser always are allowed to change the status
      if (this.superuser) {
        return true
      }

      // if there is no beheerder, everyone may change the status
      if (!this.chargingpoint.properties.beheerder) {
        return true
      }

      // is user the beheerder?
      return this.currentUserId === this.chargingpoint.properties.beheerder?.user_id
    },
    isUserCpo() {
      return this.currentUserRole === 'cpo'
    },
    isLoading() {
      return this.isDbOccupied || this.isLoadingAws
    },
    statusInfo() {
      if (this.isAllowedToEditStatus) {
        return null
      }

      const beheerderName = this.chargingpoint.properties?.beheerder?.name
      return `De status kan alleen aangepast worden door de beheerder van deze locatie (${beheerderName}).`
    },
    groupLayers() {
      return this.getPlanmodeLayers?.filter(layer =>
        layer.status !== 'hidden' &&
        this.fields.status.value.includes(layer.config.statusGroup))
    },
    layer() {
      const status = this.chargingpoint.properties.status
      const initialSelectedLayer = this.groupLayers.find(layer => layer.id.includes(status))

      return initialSelectedLayer || this.groupLayers.find(layer => layer.id.includes(this.fields.status.value))
    },
    chargingPointLabelStyle() {
      return `border-bottom: 1px solid ${this.layer.config.iconColor}`
    },
    parkinglotIds() {
      return this.chargingpoint.properties.parkinglotIds?.join(', ')
    },
    isCpoFieldDisabled() {
      if (!this.currentCpo) return false
      return this.isUserCpo && this.identity.category === 'cpo' && (this.identity.uuid !== this.currentCpo?.uuid)
    },
    publicationDate() {
      return this.fields.trafficDecision.publicationDate
    },
    definitiveDate() {
      return this.fields.trafficDecision.definitiveDate
    },
    phaseKey() {
      return this.prio?.fase || this.prio?.phase
    },
    activeCPOLabel() {
      return (this.cpoSelectOptions.find(o => o.value === this.activeCPO.value) || {}).label || '-'
    },
    activeOwnerLabel() {
      return (this.ownerOptions.find(o => o.value === this.fields.owner.value) || {}).text || this.$t('none')
    },
    activePublishNetworksLabel() {
      return this.fields.publish_allowed_to.value
        .map(uuid => (this.networkOptions.find(o => o.value === uuid) || {}).label)
        .filter(Boolean)
        .join(', ') || this.$t('none')
    },
  },
  watch: {
    // re-init when selected chargingpoint changes
    chargingpoint(next, previous) {
      if (!next || next.uuid === previous.uuid) {
        return
      }

      this.init()
    },
    'fields.fase.value': {
      handler(newValue, oldValue) {
        this.faseHasChanged = false
        if (oldValue !== undefined && newValue !== oldValue) {
          this.faseHasChanged = !this.isRealizedAndDifferentCpo
        }
      },
      immediate: true,
    },
    'fields.remark.value': {
      handler(newValue, oldValue) {
        if (oldValue !== undefined && newValue !== oldValue) {
          this.remarkHasChanged = true
        }
      },
    },
  },
  async created() {
    Object.keys(this.labels).forEach(label => this.labels[label] = `${this.translationsPath}.${label}`)
    this.init()

    await this.$_chargingpointEditMixin_connectToPusher()
  },
  beforeDestroy() {
    this.fields.status.class = ''
  },
  methods: {
    ...mapActions('planmode', ['clearAlternativeLocationForUuid']),
    init() {
      this.setInitialValues()
    },
    getNetworkByUUID(stakeholderUUID) {
      const network = this.networkOptions.find((n) => n.value === stakeholderUUID)
      return network ? { uuid: network.value, name: network.label } : null
    },
    /* fill form with data */
    setInitialValues() {
      const point = this.chargingpoint

      this.fields.address.street.value = point.address?.street || null
      this.fields.address.number.value = point.address?.number || null
      this.fields.address.suffix.value = point.address?.suffix || null
      this.fields.address.postalcode.value = point.address?.postalcode || null
      this.fields.address.city.value = point.address?.city || null
      this.fields.status.value = point.properties.status || 'suggestion'
      this.fields.fase.value = [...phaseToArray(this.phaseKey), ...phaseToArray(point.prio?.customPhase)] || []
      this.activeCPO.value = this.currentCpo ? this.currentCpo.uuid : null
      this.fields.owner.value = this.owner ? this.owner.uuid : null
      this.fields.beheerder.value = point.properties.beheerder?.user_id || null
      this.fields.power.value = this.power / 1000 || null
      this.fields.remark.value = point.properties.remark || ''
      this.newValidators = [...(point.properties.validators || [])]

      if (point.participation?.closingDates) {
        this.fields.participation.closingDate = reverseDate(point.participation.closingDates?.at(-1))
      }
      this.fields.trafficDecision = point.trafficDecision || { publicationDate: '', definitiveDate: '' }
      this.fields.publish_allowed_to.value = point.publish_allowed_to?.map(network => network.uuid) || []
      this.publishToggle = point.publish

      if (this.layer) {
        this.selectedStatus = this.layer.id
        this.chargingPointLabel = this.layer.label
        this.chargingPointCategory = this.layer.config.statusGroup
      }

      this.fields.predecessor.value = this.getRejectedLocationUuid ? { uuid: this.getRejectedLocationUuid } : (point.properties.predecessor || null)
      // reset, otherwise the rejected location is also selected for a new location
      this.clearAlternativeLocationForUuid()
      this.faseHasChanged = false
      this.remarkHasChanged = false
    },
    setChargingPointCategory(chargingPointCategory) {
      this.fields.status.value = chargingPointCategory
      this.selectedStatus = this.layer.id
      this.chargingPointLabel = this.layer.label
    },

    isPublicationDate(dateField) {
      return dateField === 'publicationDate'
    },

    handleInput(chargingPoint) {
      this.selectedStatus = chargingPoint.layer.id
      this.fields.status.value = this.selectedStatus.replace('chargingpoints-', '')
      this.chargingPointLabel = chargingPoint.layer.label
    },

    handleDatePickerInput(event, dateField) {
      if (dateField === 'publicationDate' && !event) {
        this.fields.trafficDecision.definitiveDate = ''
      }
    },

    getPhaseLabels(prio, faseOptions) {
      const faseLabels = (Array.isArray(prio.fase) ? prio?.fase : [prio.fase])?.map(v => v)
      const customPhaseLabels = (Array.isArray(prio.customPhase) ? prio?.customPhase : [prio.customPhase])?.map(v => {
        const opt = faseOptions.find(o => o.value === v)
        return opt ? opt.label : v
      })

      return [...faseLabels, ...customPhaseLabels]
    },

    async handleSubmitUpdate() {
      const point = this.chargingpoint
      const address = this.computeAddress()
      const newStatus = this.fields.status.value

      const fase = []
      const customPhase = []
      this.fields.fase.value.forEach(phase => {
        if (validate(phase)) {
          customPhase.push(phase)
        } else {
          fase.push(phase)
        }
      })

      try {
        const data = {
          user: { name: this.currentUserName, id: this.currentUserId },
          uuid: point.uuid,
          code: point.code,
          status: newStatus,
          remark: this.fields.remark.value,
          power: Math.floor((this.fields.power.value || 0) * 1000),
          cpo: this.activeCPO.value,
          owner: this.fields.owner.value,
          phase: customPhase,
          fase,
          trafficDecision: Object.values(this.fields.trafficDecision).some(v => v?.length)
            ? this.fields.trafficDecision
            : null,
          publish: this.publishToggle,
          publish_allowed_to: this.publishToggle === false && newStatus.includes('private')
            ? this.fields.publish_allowed_to.value.map(uuid => this.getNetworkByUUID(uuid)).filter(Boolean)
            : [],
          predecessor: this.fields.predecessor.value,
          beheerder: this.usersByCode.find(u => u.user_id === this.fields.beheerder.value) ?? null,
          validators: this.newValidators,
          ...(this.fields.address.edited && {
            address: address.source ? address : { ...address, source: SOURCE.GOOGLE },
          }),
        }

        this.isDbOccupied = true
        this.error = false
        this.clearAlternativeLocationForUuid()

        await this.$_chargingpointEditMixin_save({ data })

        this.isDbOccupied = false
        this.setInitialValues()
      } catch (error) {
        this.error = error.message
        this.isDbOccupied = false
      }
    },
    computeAddress() {
      const address = {}
      Object.entries(this.fields.address).forEach(field => {
        const [key, value] = field
        address[key] = value.value || ''
      })

      if (this.fields.address.edited) {
        address.edited = this.fields.address.edited
        address.source = SOURCE.MANUAL
      }

      if (!this.fields.address.simple_address) {
        address.simple_address = formatSimpleAddress({ address })
        address.formatted_address = formatFormattedAddress({ address })
      }

      return address
    },

    /*
    * Confirm that this chargingpoint is to be edited, while it already is assigned a realisation fase
    */
    unlock() {
      this.$bvModal.msgBoxConfirm('Weet u zeker dat u deze laadpaal wilt ontgrendelen?', {
        title: 'Deze laadpaal bevat een fase prioritering',
        okVariant: 'danger',
        okTitle: 'Ontgrendelen',
        cancelTitle: 'Annuleren',
      })
        .then(async (decision) => {
          if (!decision) {
            return
          }

          this.locked = false
        })
    },

    /**
     * Delete the marker from the data set
     */
    handleChargingpointDelete() {
      this.$bvModal.msgBoxConfirm(
        'Bevestig het verwijderen van dit punt.', {
        okVariant: 'danger',
        okTitle: 'Bevestigen',
        cancelTitle: 'Annuleren',
      })
        .then(confirmed => {
          if (confirmed) {
            /* $_chargingpointEditMixin_delete broadcasts to close the popup/sidepanel */
            this.$_chargingpointEditMixin_delete({
              code: this.getActiveMunicipality,
              _id: this.chargingpoint._id, // full ref
            })
          }
        })
    },
  },
}
</script>

<style lang="scss">
.unlock-hint-icon--white {
  fill: white;
}

.sticky-bottom {
  width: calc(100% - 40px - 28px);
  position: absolute;
  bottom: 0;
  background: white;
  padding: 10px;
}

.hidden {
  display: none !important;
}

.FormField {
  input {
    color: var(--text-muted);
  }

  label,
  legend {
    font-size: 1rem;
    font-weight: 600;
    text-transform: none;
    color: var(--primary-text)
  }
}

.ActionSidebar {
  .add-validator {
    .custom-select {
      width: 225px;
    }
  }
}

.add-validator {
  width: calc(100% - 26px) !important;

  &__icon {
    font-size: 25px;
  }
}

.the-validators {
  ul {
    list-style: none;
    padding: 0;
  }
}

.ratio {
  overflow: hidden;
}


.datepicker {
  .b-calendar .b-calendar-inner {
    min-width: auto !important;
    width: auto !important;

    .b-calendar-grid-help {
      display: none;
    }
  }
}

@keyframes wobbel {
  0% {
    transform: rotate(0);
  }

  25% {
    transform: rotate(-2deg);
  }

  50% {
    transform: rotate(0);
  }

  75% {
    transform: rotate(2deg);
  }

  100% {
    transform: rotate(0);
  }
}
</style>
